import {
    hasSurvey,
    ORDER_LIFECYCLE_CODES,
    ORDER_STATUS_LABELS,
    type Order,
    type OrderStatusCode,
} from './model';

/**
 * De levenscyclus van een keuring: zeven kaartjes, in deze volgorde, op
 * Keuringen (scherm 5) en in het Dossier (scherm 8). De fase volgt de status -
 * alles ervóór is `done`, de status zelf is `now`, alles erna is `todo`. Een
 * geannuleerde keuring heeft geen `now`: wat al gebeurd was blijft `done`.
 *
 * Welke stap je vanaf hier mag zetten staat níet hier: dat komt uit
 * `allowed_transitions` in de payload van de API.
 */
export type LifecyclePhase = 'done' | 'now' | 'todo';

export interface LifecycleStep {
    status: OrderStatusCode;
    label: string;
    phase: LifecyclePhase;
    /** Het moment dat bij deze stap hoort, als de keuring het kent. */
    at: string | null;
    /** Mag de keuring vanaf nu naar deze stap? Volgt `allowed_transitions`. */
    allowed: boolean;
}

/** Positie in de levenscyclus; -1 voor `cancelled`. */
export function lifecycleIndexFor(status: OrderStatusCode): number {
    return (ORDER_LIFECYCLE_CODES as readonly OrderStatusCode[]).indexOf(status);
}

/**
 * De huidige stap. Voor een geannuleerde keuring: de eerste stap die níet meer
 * gezet is, afgeleid uit haar eigen tijdstippen - een afspraak betekent dat hij
 * ingepland was, anders kwam hij niet verder dan de aanvraag.
 */
function reachedIndex(order: Pick<Order, 'status' | 'scheduledAt'>): number {
    const index = lifecycleIndexFor(order.status);

    if (index >= 0) {
        return index;
    }

    return lifecycleIndexFor(order.scheduledAt ? 'scheduled' : 'new') + 1;
}

function momentFor(
    status: OrderStatusCode,
    order: Pick<Order, 'requestedAt' | 'scheduledAt' | 'completedAt'>,
): string | null {
    switch (status) {
        case 'new':
            return order.requestedAt;
        case 'scheduled':
            return order.scheduledAt;
        case 'completed':
            return order.completedAt;
        default:
            return null;
    }
}

export function lifecycleSteps(
    order: Pick<
        Order,
        'status' | 'allowedTransitions' | 'requestedAt' | 'scheduledAt' | 'completedAt'
    >,
): LifecycleStep[] {
    const cancelled = order.status === 'cancelled';
    const current = reachedIndex(order);

    return ORDER_LIFECYCLE_CODES.map((status, index) => {
        const phase: LifecyclePhase =
            index < current ? 'done' : index === current && !cancelled ? 'now' : 'todo';

        return {
            status,
            label: ORDER_STATUS_LABELS[status],
            phase,
            at: phase === 'todo' ? null : momentFor(status, order),
            allowed: order.allowedTransitions.includes(status),
        };
    });
}

/** Volgnummer op het kaartje: `01` … `07`. */
export function lifecycleNumber(index: number): string {
    return String(index + 1).padStart(2, '0');
}

/**
 * Wat er al naar de klant ging. Volgt uit de status - een keuring die
 * `scheduled` staat heeft per definitie een bevestiging én een afspraak gehad.
 */
export interface SentMilestones {
    confirmation: boolean;
    appointment: boolean;
    report: boolean;
    invoice: boolean;
}

/**
 * Staat de opname van deze keuring op een apparaat? Zolang de keuring ingepland of
 * in opname is (`SyncScope` in de API): daarvóór is er niets in te vullen, daarna is
 * de opname ingediend en staat wat eruit volgt in het rapport. En bij een afgeronde
 * keuring waarvan een opname weer open staat voor een correctie
 * ({@link surveyReopenedAfterSending}).
 */
export function surveyOnDevice(order: Pick<Order, 'status' | 'services'>): boolean {
    return (
        order.status === 'scheduled' ||
        order.status === 'surveying' ||
        surveyReopenedAfterSending(order)
    );
}

/**
 * Staat een opname van deze keuring weer open nadat het rapport de deur uit ging?
 *
 * De keuring blijft dan `completed` of `invoiced` - het rapport is bij de koper, het
 * tegoed verbruikt, de factuur blijft zoals ze is - dus de status zegt het niet. Een
 * opname met vragenlijst die begonnen is en niet (meer) is ingediend, zegt het wel:
 * vóór verzending was elke opname ingediend. Dezelfde regel als `SyncScope` in de API.
 */
export function surveyReopenedAfterSending(order: Pick<Order, 'status' | 'services'>): boolean {
    return (
        (order.status === 'completed' || order.status === 'invoiced') &&
        order.services.some(
            (service) =>
                hasSurvey(service) &&
                service.status !== 'cancelled' &&
                (service.inspection.status === 'in_progress' ||
                    service.inspection.status === 'completed'),
        )
    );
}

/** Kan deze keuring een rapport hebben? Een rapport bestaat vanaf het concept. */
export function reportStageReached(order: Pick<Order, 'status'>): boolean {
    return lifecycleIndexFor(order.status) >= lifecycleIndexFor('draft_report');
}

export function sentMilestones(order: Pick<Order, 'status' | 'scheduledAt'>): SentMilestones {
    const index = lifecycleIndexFor(order.status);
    const past = (status: OrderStatusCode) => index >= lifecycleIndexFor(status);

    return {
        confirmation: past('confirmed') || order.scheduledAt !== null,
        appointment: order.scheduledAt !== null,
        report: past('completed'),
        invoice: past('invoiced'),
    };
}
